<template>
  <section
    v-if="compact"
    class="relative flex min-h-13 phone:min-h-auto phone:mb-1.5 items-center gap-3"
    :class="[isClickable ? 'cursor-pointer' : '']"
    :aria-label="$t('common.quotedMessage')"
    data-test="quote-block"
  >
    <wui-icon
      :name="compactIconName"
      class="shrink-0 text-surf-brand-basedarkblue-def"
    />
    <div
      v-if="compactFile"
      class="size-9 shrink-0 overflow-hidden rounded-8"
      data-test="quote-compact-file"
    >
      <file-icon
        class="h-full w-full"
        :cookie="compactFile.filecookie"
        :mime="compactFile.filemime ?? ''"
        :name="compactFile.filename"
        :open-preview-on-click="false"
        :object-fit="isImageFile(compactFile.filename) ? 'cover' : 'contain'"
        :bordered="false"
      />
    </div>
    <div class="min-w-0 grow">
      <p class="truncate text-label-m-12-18 text-surf-brand-basedarkblue-def">
        {{ compactAuthor }}
      </p>
      <p
        v-if="compactPreviewText"
        class="truncate text-label-r-12-18 text-fg-base-fg10"
      >
        {{ compactPreviewText }}
      </p>
    </div>
    <slot />
  </section>

  <section
    v-else
    class="relative grid rounded-8 border-l-3 border-l-surf-brand-basedarkblue-def bg-bg-base-bg2 py-1.5 pr-2 pl-2"
    :class="[!!slots.default ? 'grid-cols-[1fr_40px]' : 'grid-cols-[1fr]', isClickable ? 'cursor-pointer' : '']"
    :aria-label="$t('common.quotedMessage')"
    data-test="quote-block"
  >
    <div class="z-9 flex items-start gap-2">
      <div
        v-if="file"
        class="shrink-0 self-start"
      >
        <picture
          class="flex h-10 w-10 flex-wrap items-start"
          @click.stop="onImageClick(file)"
        >
          <file-icon
            :cookie="file.filecookie"
            :mime="file.filemime ?? ''"
            :name="file.filename"
            :event="event"
          />
        </picture>
      </div>
      <div
        class="grow"
      >
        <div
          class="grid text-1316 font-light pr-2"
        >
          <p
            class="flex items-center truncate text-label-r-12-18 text-surf-brand-basedarkwht-def"
          >
            <span class="truncate">{{ author }}</span>
            <span>{{ ', ' + eventTime }}</span>
          </p>
          <p
            v-if="textToShow"
            ref="textElement"
            class="font-normal whitespace-pre-wrap wrap-break-word text-fg-base-fg10 text-label-r-12-18 [&_a]:text-comp-link-neut-def [&_b[data-contact-uri]]:cursor-pointer [&_b[data-contact-uri]]:font-normal [&_b[data-contact-uri]]:text-surf-brand-basedarkblue-def"
            :class="{ 'line-clamp-1': !canExpandText }"
            v-html="textToShow"
          />
          <div
            v-if="canExpandText && isTextTruncated"
            class="cursor-pointer flex items-center gap-1 pr-1"
            @click="toggleExpand"
          >
            <p class="text-label-r-12-18 text-comp-link-neut-def hover:underline">
              {{ isExpanded ? $t('common.collapse') : $t('common.expand') }}
            </p>
            <wui-icon
              :name="isExpanded ? 'chevronUpSmM' : 'chevronDownSmM'"
              class="text-comp-link-neut-def h-5! w-5!"
            />
          </div>
        </div>
      </div>
    </div>
    <slot />
  </section>
</template>

<script setup lang="ts">
import { IconName, WuiIcon } from '@wui/common-library'
import { HistoryMessageItem, MessageOutgoingEvent, MessageIncomingEvent, IMFile } from '@wui/im'
import { computed, useSlots, onMounted, watch, nextTick } from 'vue'


import { useOpenAttachmentViewer } from '@/widgets/media-with-thread-chat'

import { FileIcon } from '@/entities/chat-event-file'
import { useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'
import { isVoiceMessage } from '@/entities/voice-message'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useTextExpand } from '@/shared/composables/use-text-expand'
import { getUnknownContactLabel } from '@/shared/utils/contacts'
import { convertTimestampToDateTimeString } from '@/shared/utils/date-time'
import { isImageFile, isImFileImageOrVideo } from '@/shared/utils/files'
import { sanitizeToText } from '@/shared/utils/sanitize'
import { formatMessageText } from '@/shared/utils/text-entities'

import { getForwardingAuthorName } from '../../forward'
import { useMessageFilesPreview } from '../../lib/use-message-files-preview'

/* eslint-disable vue/require-default-prop -- selectedText/id omitted from defaults to satisfy exactOptionalPropertyTypes */
const props = withDefaults(defineProps<{
  event: HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>,
  selectedText?: string,
  isClickable?: boolean,
  canExpandText?: boolean
  id?: number
  compact?: boolean
}>(), {
  isClickable: false,
  canExpandText: false,
  compact: false,
})
/* eslint-enable vue/require-default-prop */

const { getFilesText } = useMessageFilesPreview()
const { currentUser } = useCurrentUser()
const { t } = useLocalization()
const { findByPServed } = useStoreContacts()
const { openAttachmentViewer } = useOpenAttachmentViewer()
const { findGuestByPserved } = useStoreRooms()
const slots = useSlots()

const onImageClick = (file: IMFile) => {
  if (isImFileImageOrVideo(file)) {
    void openAttachmentViewer(file, props.event)
  }
}

const eventTime = computed(() => convertTimestampToDateTimeString(props.event.timestamp))
const contact = computed(() => findByPServed(props.event.item.executor))
const textToShow = computed(() => {
  if (isVoiceMessage(props.event.item)) {
    return t('mainAside.voiceDraft')
  }

  if (props.selectedText) {
    return props.selectedText
  }

  let text = formatMessageText(props.event.item.messageText, props.event.item.entities)
  if (!text) {
    const forwarding = props.event.item.forwarding
    if (forwarding?.forwardingText) {
      text = forwarding?.forwardingText
    } else if (forwarding?.forwardingFiles?.length) {
      text = getFilesText(forwarding?.forwardingFiles?.length)
    }
  }

  return text
})

const {
  isExpanded,
  textElement,
  isTextTruncated,
  initTextExpanding,
  toggleExpand,
} = useTextExpand(textToShow)

const files = computed(() => props.event.item.files ?? [])
const guestDisplayName = computed(() => {
  const executor = props.event.item.executor
  if (!executor.includes('<sip:GUEST')) {
    return undefined
  }
  const name = findGuestByPserved(props.event.item.pServed, executor)
  return name ? `${name} (${t('common.guest')})` : undefined
})
const author = computed(() => {
  if (props.event.item.type === 'OutgoingMessageEvent') {
    return guestDisplayName.value ?? currentUser.value?.name ?? ''
  }
  return contact.value?.name ?? guestDisplayName.value ?? getUnknownContactLabel(props.event.item.executor)
})

const compactAuthor = computed(() => {
  // Reply to a forwarded bubble → show the forwarder, not the original author
  if (props.event.item.forwarding?.forwardingEventId) {
    if (props.event.item.type === 'OutgoingMessageEvent') {
      return t('common.you')
    }
    return getForwardingAuthorName(props.event)
  }

  if (props.event.item.type === 'OutgoingMessageEvent') {
    return t('common.you')
  }
  return author.value
})

const isPartialQuote = computed(() => {
  const selected = sanitizeToText(props.selectedText ?? '').trim()
  if (!selected) return false
  const full = sanitizeToText(props.event.item.messageText ?? '').trim()
  return selected !== full
})

const compactIconName = computed<IconName>(() =>
  isPartialQuote.value ? 'quoteM' : 'arrowAnswerM',
)

const compactPreviewText = computed(() => {
  if (isVoiceMessage(props.event.item)) {
    return t('mainAside.voiceDraft')
  }

  if (props.selectedText) {
    return sanitizeToText(props.selectedText).trim()
  }

  const text = sanitizeToText(props.event.item.messageText ?? '').trim()
  if (text) return text

  const forwarding = props.event.item.forwarding
  if (forwarding?.forwardingText) {
    return sanitizeToText(forwarding.forwardingText).trim()
  }
  if (forwarding?.forwardingFiles?.length) {
    return getFilesText(forwarding.forwardingFiles.length)
  }
  if (files.value.length) {
    return getFilesText(files.value.length)
  }

  return ''
})

const file = computed(() => {
  if (isVoiceMessage(props.event.item)) {
    return
  }

  const image = files.value.find((file) => isImageFile(file.filename))
  return image ?? files.value.find((file) => !isImageFile(file.filename))
})

const compactFile = computed(() => {
  if (isVoiceMessage(props.event.item)) return undefined
  return files.value[0]
})

onMounted(() => {
  if (props.canExpandText) {
    initTextExpanding()
  }
})

watch(() => props.id, async (n, o) => {
  if (n !== o && n) {
    await nextTick()

    initTextExpanding()
  }
})
</script>
